import React from "react";
import { useTheme } from "@mui/material/styles";
import { Variants, SizeStyles } from "./TextField.variants";
import {
  CustomInputLabel,
  CustomTextField,
  TextFieldContainer
} from "./TextField.styles";
import { TextFieldProps as MuiTextFieldProps, SxProps } from "@mui/material";
import { InfoMessage } from "..";

export interface CustomVariantTextFieldProps
  extends Pick<
    MuiTextFieldProps,
    | "value"
    | "onChange"
    | "onBlur"
    | "inputRef"
    | "InputProps"
    | "inputProps"
    | "onKeyDown"
    | "fullWidth"
    | "helperText"
    | "required"
    | "onBlurCapture"
    | "autoFocus"
    | "autoComplete"
    | "id"
  > {
  variant?: "primary" | "secondary";
  type?: "text" | "email" | "password";
  size?: "sm" | "md" | "lg";
  label?: string;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  errorBackground?: boolean;
  disabled?: boolean;
  leftIcon?: React.ReactElement | null;
  rightIcon?: React.ReactElement | null;
  disabledIcon?: React.ReactElement | null;
  customInputLabelSx?: SxProps;
  errorMessageSx?: SxProps;
  sx?: SxProps;
}

export const TextField = ({
  variant = "primary",
  type = "text",
  size = "md",
  label,
  placeholder,
  error = false,
  errorMessage = "",
  errorBackground = false,
  disabled = false,
  disabledIcon = null,
  leftIcon = null,
  rightIcon = null,
  fullWidth,
  autoComplete = "off",
  customInputLabelSx,
  errorMessageSx,
  sx,
  ...rest
}: CustomVariantTextFieldProps) => {
  const theme = useTheme();

  const variantStylesFn = Variants(theme);
  const sizeStylesFn = SizeStyles(theme);

  return (
    <TextFieldContainer sx={fullWidth ? {width: '100%'} : {}}>
      {label && (
        <CustomInputLabel sx={customInputLabelSx} shrink htmlFor="custom-textfield-input">
          {label}
        </CustomInputLabel>
      )}
      <CustomTextField
        type={type}
        id="custom-textfield-input"
        disabled={disabled}
        placeholder={placeholder}
        InputProps={{
          startAdornment: leftIcon,
          endAdornment: disabled && disabledIcon ? disabledIcon : rightIcon,
        }}
        sx={{
          ...variantStylesFn[variant],
          ...sizeStylesFn[size],
          ...sx,
        }}
        {...rest}
        inputProps={{
          ...rest.inputProps,
          autoComplete
        }}
        className={error ? "is-error" : ""}
      />
      {error && errorMessage != "" && (
        <InfoMessage hasBackground={errorBackground} hasIcon={false} variant="error" message={errorMessage} sx={errorMessageSx}/>
      )}
    </TextFieldContainer>
  );
};

export interface TextFieldProps extends React.ComponentProps<typeof TextField> {
  as?: React.ElementType;
}

TextField.displayName = "TextField";
